// src/pages/ProductDetail.jsx
import React, { useEffect, useState, useContext } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { CartContext } from '../context/CartContext';
import ProductCard from '../components/product/ProductCard';
import AvisProduit from '../components/product/AvisProduit';
import SEOHelper from '../components/common/SEOHelper';

function ProductDetail() {
  const { t, i18n } = useTranslation();
  const { id } = useParams();
  const { updateCartItemCount } = useContext(CartContext) || {};

  const [product, setProduct]       = useState(null);
  const [loading, setLoading]       = useState(true);
  const [error, setError]           = useState(null);
  const [activeImg, setActiveImg]   = useState(0);

  const [selectedSize, setSelectedSize]     = useState('');
  const [selectedColor, setSelectedColor]   = useState('');
  const [quantity, setQuantity]             = useState(1);

  const [adding, setAdding]     = useState(false);
  const [added, setAdded]       = useState(false);
  const [showSizeGuide, setShowSizeGuide] = useState(false);

  /* =========================================================
     CHARGEMENT PRODUIT
  ========================================================= */
  useEffect(() => {
    let mounted = true;
    setLoading(true);
    axios
      .get(`https://madadline.com/api/products/${id}`)
      .then(res => {
        if (!mounted) return;
        setProduct(res.data);
        setError(null);
      })
      .catch(() => {
        if (mounted) setError('not_found');
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });
    return () => { mounted = false; };
  }, [id]);

  useEffect(() => {
    setActiveImg(0);
    setSelectedSize('');
    setSelectedColor('');
    setQuantity(1);
  }, [id]);

  /* =========================================================
     CALCULS
  ========================================================= */
  const variants = product?.variants || [];
  const sizes  = [...new Set(variants.map(v => v.size).filter(Boolean))];
  const colors = [...new Set(variants.map(v => v.color).filter(Boolean))];
  const sizesFinal  = sizes.length  > 0 ? sizes  : ['S/M', 'L/XL'];
  const colorsFinal = colors.length > 0 ? colors : (product?.couleurs || product?.colors || []);

  const selectedVariant = variants.find(
    v => v.size === selectedSize && v.color === selectedColor
  );
  const variantStock = selectedVariant ? Number(selectedVariant.stock) : 0;

  const originalPrice = Number(product?.prix ?? 0);
  const discount      = Number(product?.discount ?? 0);
  const newPrice      = product?.new_price != null ? Number(product.new_price) : null;
  const hasPromo      = discount > 0 && newPrice != null;
  const price         = hasPromo ? newPrice : originalPrice;

  const isRequestOnly = Boolean(product?.is_request_only);

  const stock = (product?.variants && product.variants.length > 0)
    ? product.variants.reduce((sum, v) => sum + (Number(v.stock) || 0), 0)
    : Number(product?.stock ?? 0);

  /* =========================================================
     HELPERS
  ========================================================= */
  const colorToHex = (colorName) => {
    if (!colorName) return '#e5e5e5';
    const map = {
      'noir': '#000', 'black': '#000',
      'blanc': '#fff', 'white': '#fff',
      'rouge': '#DC2626', 'red': '#DC2626',
      'rose': '#E8B4C6', 'pink': '#E8B4C6',
      'bleu': '#2563EB', 'blue': '#2563EB',
      'vert': '#16A34A', 'green': '#16A34A',
      'jaune': '#EAB308', 'yellow': '#EAB308',
      'orange': '#F97316',
      'violet': '#7C3AED', 'purple': '#7C3AED',
      'marron': '#78350F', 'brown': '#78350F',
      'beige': '#F5E6D3',
      'gris': '#6B7280', 'gray': '#6B7280', 'grey': '#6B7280',
      'bordeaux': '#7F1D1D',
      'kaki': '#78716C',
      'creme': '#FDFAF7', 'crème': '#FDFAF7',
      'ivoire': '#FFFFF0',
      'dore': '#D4AF37', 'doré': '#D4AF37',
      'argent': '#C0C0C0', 'silver': '#C0C0C0',
    };
    return map[colorName.toLowerCase().trim()] || '#e5e5e5';
  };

  /* =========================================================
     ACTIONS
  ========================================================= */
  const handleAddToCart = async () => {
    if (!selectedSize) { alert('Veuillez choisir une taille.'); return; }
    if (!selectedColor) { alert('Veuillez choisir une couleur.'); return; }
    if (selectedVariant && variantStock <= 0) {
      alert('Cette combinaison est épuisée.');
      return;
    }

    setAdding(true);
    try {
      const token = localStorage.getItem('auth_token');

      if (token) {
        await axios.post(
          'https://madadline.com/api/cart',
          { product_id: product.id, quantity, size: selectedSize, color: selectedColor },
          { headers: { Authorization: `Bearer ${token}` } }
        );
      } else {
        const cart = JSON.parse(localStorage.getItem('cart') || '[]');
        const existingIndex = cart.findIndex(
          i => i.product.id === product.id && i.size === selectedSize && i.color === selectedColor
        );

        if (existingIndex >= 0) {
          cart[existingIndex].quantity += quantity;
        } else {
          cart.push({
            id: `local-${Date.now()}`,
            product,
            quantity,
            size: selectedSize,
            color: selectedColor,
          });
        }

        localStorage.setItem('cart', JSON.stringify(cart));
      }

      setAdded(true);
      if (typeof updateCartItemCount === 'function') updateCartItemCount();
      setTimeout(() => setAdded(false), 2500);

      window.dispatchEvent(new Event('open-cart-drawer'));
    } catch (e) {
      console.error(e);
      alert("Erreur lors de l'ajout au panier");
    } finally {
      setAdding(false);
    }
  };

  const handleOrderRequest = () => {
    if (!selectedSize)  { alert('Veuillez choisir une taille.');  return; }
    if (!selectedColor) { alert('Veuillez choisir une couleur.'); return; }
    if (!isRequestOnly && selectedVariant && variantStock <= 0) {
      alert('Cette combinaison est épuisée.');
      return;
    }

    const oneShotItem = {
      id: `oneshot-${Date.now()}`,
      product,
      quantity,
      size: selectedSize,
      color: selectedColor,
      material: null,
    };

    window.dispatchEvent(new CustomEvent('open-checkout-request', {
      detail: { items: [oneShotItem] },
    }));
  };

  const handleWhatsAppOrder = () => {
    if (!product) return;
    const name = i18n.language === 'en' && product.nom_en ? product.nom_en : product.nom;
    let msg = `${t('pdp.waMessageIntro')}\n\n`;
    msg += `${name}\n`;

    if (isRequestOnly) {
      msg += `${t('pdp.waPrice')} : ${t('pdp.priceOnRequest')}\n`;
    } else {
      msg += `${t('pdp.waPrice')} : ${price.toFixed(0)} DH`;
      if (hasPromo) msg += ` ${t('pdp.waInsteadOf', { price: originalPrice.toFixed(0) })}`;
      msg += `\n`;
    }

    if (selectedSize)  msg += `${t('pdp.waSize')} : ${selectedSize}\n`;
    if (selectedColor) msg += `${t('pdp.waColor')} : ${selectedColor}\n`;
    if (quantity > 1)  msg += `${t('pdp.waQuantity')} : ${quantity}\n`;

    window.open(`https://wa.me/212669428878?text=${encodeURIComponent(msg)}`, '_blank');
  };

  /* =========================================================
     RENDER — ÉTATS
  ========================================================= */
  if (loading) {
    return (
      <main className="pdp">
        <div className="pdp__loading"><div className="spinner" /></div>
      </main>
    );
  }
  if (error || !product) {
    return (
      <main className="pdp">
        <div className="pdp__error">
          <h2>{t('pdp.notFound')}</h2>
          <Link to="/boutique" className="btn btn-primary">{t('pdp.backToShop')}</Link>
        </div>
      </main>
    );
  }

  const name = i18n.language === 'en' && product.nom_en ? product.nom_en : product.nom;
  const desc = i18n.language === 'en' && product.description_en
    ? product.description_en
    : product.description;

  const getImageUrl = (img) => {
    if (!img) return '';
    if (/^https?:\/\//i.test(img)) return img;
    const path = img.replace(/^\/+/, '');
    const filename = path.split('/').pop();
    if (path.startsWith('images/')) {
      return `https://madadline.com/api/images/${filename}`;
    }
    return `https://madadline.com/${path}`;
  };

  const gallery = [
    getImageUrl(product.image || product.image_url),
    getImageUrl(product.image2),
    getImageUrl(product.image3),
    ...(Array.isArray(product.images) ? product.images.map(getImageUrl) : []),
  ].filter(Boolean);

  return (
    <main className="pdp">

      <SEOHelper
        title={product.meta_title || product.nom}
        description={product.meta_description || (product.description || '').substring(0, 160)}
      />

      <nav className="pdp__breadcrumb">
        <Link to="/">{t('pdp.breadcrumbHome')}</Link>
        <span>›</span>
        <Link to="/boutique">{t('pdp.breadcrumbShop')}</Link>
        <span>›</span>
        <span className="pdp__breadcrumb-current">{name}</span>
      </nav>

      <section className="pdp__main">
        <div className="pdp__gallery">
          <div className="pdp__thumbs">
            {gallery.map((img, i) => (
              <button
                key={i}
                className={`pdp__thumb ${i === activeImg ? 'is-active' : ''}`}
                onClick={() => setActiveImg(i)}
              >
                <img src={img} alt={t('pdp.viewN', { n: i + 1 })} loading="lazy" />
              </button>
            ))}
          </div>

          <div className="pdp__hero">
            <img src={gallery[activeImg]} alt={name} loading="eager" />

            {isRequestOnly ? (
              <span className="pdp__badge pdp__badge--request">
                {t('pdp.requestBadge')}
              </span>
            ) : hasPromo ? (
              <span className="pdp__badge pdp__badge--promo">-{discount}%</span>
            ) : stock > 0 && stock <= 3 ? (
              <span className="pdp__badge pdp__badge--low">
                {t('pdp.lowStock', { n: stock })}
              </span>
            ) : stock === 0 ? (
              <span className="pdp__badge pdp__badge--out">
                {t('pdp.outOfStock')}
              </span>
            ) : null}
          </div>
        </div>

        <div className="pdp__info">
          <h1 className="pdp__name">{name}</h1>

          {/* ⭐ Prix : "Sur demande" si request_only */}
          {isRequestOnly ? (
            <div className="pdp__price-wrap pdp__price-wrap--request">
              <span className="pdp__price pdp__price--request">
                {t('pdp.priceOnRequest')}
              </span>
              <span className="pdp__price-hint">
                {t('pdp.priceOnRequestHint')}
              </span>
            </div>
          ) : hasPromo ? (
            <div className="pdp__price-wrap pdp__price-wrap--promo">
              <span className="pdp__price pdp__price--promo">
                {newPrice.toFixed(0)} DH
              </span>
              <span className="pdp__price pdp__price--old">
                {originalPrice.toFixed(0)} DH
              </span>
              <span className="pdp__price-save">
                {t('pdp.save', { amount: (originalPrice - newPrice).toFixed(0) })}
              </span>
            </div>
          ) : (
            <div className="pdp__price-wrap">
              <span className="pdp__price">{originalPrice.toFixed(0)} DH</span>
            </div>
          )}

          {desc && <p className="pdp__desc">{desc}</p>}

          {/* =========================================================
              ⭐ SÉLECTEUR COULEUR — Pills stylisées
          ========================================================= */}
          {colorsFinal.length > 0 && (
            <div className="pdp__section">
              <div className="pdp__label-row">
                <label className="pdp__label">
                  {t('pdp.labelColor')}
                  {selectedColor && (
                    <span className="pdp__label-value"> — {selectedColor}</span>
                  )}
                </label>
              </div>

              <div className="pdp__color-options">
                {colorsFinal.map((c, i) => {
                  const label = typeof c === 'object' ? c.name : c;
                  const hex = typeof c === 'object'
                    ? (c.hex || c.code || colorToHex(label))
                    : colorToHex(label);
                  const isActive = selectedColor === label;

                  return (
                    <button
                      key={i}
                      type="button"
                      className={`pdp__color-pill ${isActive ? 'is-active' : ''}`}
                      onClick={() => setSelectedColor(label)}
                      title={label}
                      aria-label={label}
                      aria-pressed={isActive}
                    >
                      <span
                        className="pdp__color-dot"
                        style={{ background: hex }}
                      />
                      <span className="pdp__color-name">{label}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* =========================================================
              ⭐ SÉLECTEUR TAILLE — Pills stylisées
          ========================================================= */}
          <div className="pdp__section">
            <div className="pdp__label-row">
              <label className="pdp__label">{t('pdp.labelSize')}</label>
              <button
                className="pdp__guide-link"
                onClick={() => setShowSizeGuide(true)}
                type="button"
              >
                {t('pdp.sizeGuide')}
              </button>
            </div>

            <div className="pdp__size-options">
              {sizesFinal.map(s => {
                const isActive = selectedSize === s;

                // Désactive les tailles dont toutes les variantes sont en rupture
                const variantsForSize = variants.filter(v => v.size === s);
                const sizeStock = variantsForSize.reduce(
                  (sum, v) => sum + (Number(v.stock) || 0),
                  0
                );
                const isSoldOut = !isRequestOnly && variants.length > 0 && sizeStock === 0;

                return (
                  <button
                    key={s}
                    type="button"
                    className={`pdp__size-pill ${isActive ? 'is-active' : ''} ${isSoldOut ? 'is-soldout' : ''}`}
                    onClick={() => !isSoldOut && setSelectedSize(s)}
                    disabled={isSoldOut}
                    aria-pressed={isActive}
                  >
                    {s}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Stock variante */}
          {selectedSize && selectedColor && !isRequestOnly && (
            <p
              className="pdp__variant-stock"
              style={{ color: variantStock > 0 ? '#16A34A' : '#DC2626' }}
            >
              {selectedVariant
                ? variantStock > 0
                  ? t('pdp.inStock', { n: variantStock })
                  : t('pdp.variantSoldOut')
                : t('pdp.variantUnavailable')}
            </p>
          )}

          {/* =========================================================
              QUANTITÉ
          ========================================================= */}
          <div className="pdp__section">
            <label className="pdp__label">{t('pdp.labelQty')}</label>
            <div className="pdp__qty">
              <button
                className="pdp__qty-btn"
                onClick={() => setQuantity(q => Math.max(1, q - 1))}
                type="button"
              >−</button>
              <span className="pdp__qty-value">{quantity}</span>
              <button
                className="pdp__qty-btn"
                onClick={() => setQuantity(q => q + 1)}
                type="button"
              >+</button>
            </div>
          </div>

          {/* =========================================================
              ACTIONS
          ========================================================= */}
          <div className="pdp__actions">
            {!isRequestOnly && (
              <button
                className={`pdp__btn-primary ${added ? 'is-added' : ''}`}
                onClick={handleAddToCart}
                disabled={adding || stock === 0}
              >
                <span>
                  {stock === 0
                    ? t('pdp.outOfStock')
                    : adding
                      ? t('pdp.adding')
                      : added
                        ? t('pdp.added')
                        : t('pdp.addToCart')}
                </span>
              </button>
            )}

            <button
              type="button"
              className="pdp__btn-request"
              onClick={handleOrderRequest}
              disabled={adding || (!isRequestOnly && stock === 0)}
              style={isRequestOnly ? { flexBasis: '100%' } : undefined}
            >
              {t('pdp.orderOnRequest')}
            </button>

            {!isRequestOnly && (
              <button
                className="pdp__btn-secondary"
                onClick={handleWhatsAppOrder}
                type="button"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51l-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.71.306 1.263.489 1.694.625.712.227 1.36.195 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/>
                </svg>
                {t('pdp.orderWhatsapp')}
              </button>
            )}
          </div>

          {/* ⭐ Bandeau explicatif produit request_only */}
          {isRequestOnly && (
            <div className="pdp__request-banner">
              ⓘ {t('pdp.requestOnlyNotice')}
            </div>
          )}

          <ul className="pdp__reassure">
            <li>{t('pdp.reassure1')}</li>
            <li>{t('pdp.reassure2')}</li>
            <li>{t('pdp.reassure3')}</li>
            <li>{t('pdp.reassure4')}</li>
          </ul>
        </div>
      </section>

      <section className="pdp__reviews" id="avis">
        <AvisProduit productId={product.id} />
      </section>

      {product.suggestions && product.suggestions.length > 0 && (
        <section className="pdp__suggest">
          <h2>{t('pdp.suggestTitle')}</h2>
          <div className="grid-products">
            {product.suggestions.slice(0, 4).map(p => (
              <ProductCard key={p.id} product={p} />
            ))}
          </div>
        </section>
      )}

      {/* =========================================================
          STICKY MOBILE
      ========================================================= */}
      <div className="pdp__sticky">
        <div className="pdp__sticky-info">
          <span className="pdp__sticky-name">{name}</span>
          <div className="pdp__sticky-prices">
            {isRequestOnly ? (
              <span className="pdp__sticky-price pdp__sticky-price--request">
                {t('pdp.priceOnRequest')}
              </span>
            ) : (
              <>
                <span className="pdp__sticky-price">{price.toFixed(0)} DH</span>
                {hasPromo && (
                  <span className="pdp__sticky-old">{originalPrice.toFixed(0)} DH</span>
                )}
              </>
            )}
          </div>
        </div>

        {!isRequestOnly ? (
          <>
            <button
              className="pdp__sticky-btn"
              onClick={handleAddToCart}
              disabled={adding || stock === 0}
              type="button"
            >
              {stock === 0 ? t('pdp.outOfStock') : added ? '✓' : t('pdp.stickyAdd')}
            </button>
            <button
              type="button"
              className="pdp__sticky-btn pdp__sticky-btn--request"
              onClick={handleOrderRequest}
              disabled={adding || stock === 0}
            >
              {t('pdp.orderOnRequestShort')}
            </button>
          </>
        ) : (
          <button
            type="button"
            className="pdp__sticky-btn pdp__sticky-btn--request"
            onClick={handleOrderRequest}
            disabled={adding}
            style={{ flex: 1, marginLeft: 0 }}
          >
            {t('pdp.orderOnRequest')}
          </button>
        )}
      </div>

      {/* =========================================================
          MODAL GUIDE DES TAILLES
      ========================================================= */}
      {showSizeGuide && (
        <div className="pdp__modal" onClick={() => setShowSizeGuide(false)}>
          <div className="pdp__modal-inner" onClick={e => e.stopPropagation()}>
            <button className="pdp__modal-close" onClick={() => setShowSizeGuide(false)}>✕</button>
            <h3>{t('pdp.sizeGuideTitle')}</h3>
            <table className="pdp__size-table">
              <thead>
                <tr>
                  <th>{t('pdp.thSize')}</th>
                  <th>{t('pdp.thChest')}</th>
                  <th>{t('pdp.thLength')}</th>
                  <th>{t('pdp.thHeight')}</th>
                </tr>
              </thead>
              <tbody>
                <tr><td>S/M</td><td>90–98 cm</td><td>105 cm</td><td>155–165 cm</td></tr>
                <tr><td>L/XL</td><td>98–106 cm</td><td>108 cm</td><td>160–170 cm</td></tr>
                <tr><td>XXL/XXXL</td><td>106–118 cm</td><td>110 cm</td><td>165–175 cm</td></tr>
              </tbody>
            </table>
            <p className="pdp__modal-help">{t('pdp.sizeGuideHelp')}</p>
            <a
              href="https://wa.me/212669428878"
              target="_blank"
              rel="noreferrer"
              className="btn btn-primary"
            >
              {t('pdp.sizeGuideWhatsapp')}
            </a>
          </div>
        </div>
      )}

    </main>
  );
}

export default ProductDetail;